import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Save, ArrowRightCircle, Info } from 'lucide-react';
import styles from './Toast.module.css';

export type ToastType = 'success' | 'save' | 'info' | 'complete';

interface ToastProps {
  message: string;
  isVisible: boolean;
  type?: ToastType;
  onClose: () => void;
  duration?: number;
}

export const Toast = ({ message, isVisible, type = 'success', onClose, duration = 2500 }: ToastProps) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'save': return <Save size={18} />;
      case 'complete': return <ArrowRightCircle size={18} />;
      case 'info': return <Info size={18} />;
      default: return <CheckCircle2 size={18} />;
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 50, x: '-50%', scale: 0.9 }}
          animate={{ opacity: 1, y: 0, x: '-50%', scale: 1 }}
          exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
          className={`${styles.toast} ${styles[type]}`}
        >
          <div className={styles.icon}>{getIcon()}</div>
          <span>{message}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
